import { SplitSupplyPartnerNoticeImportIssueReason } from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'

/**
 * 通告値の取込 (ファイルを選ぶ → 確認 → 取り込む) の、 他社の通告値と自社の通告値で共通の画面側の決まり。
 * 読み取り・突合・誤りの判定は backend が持ち、 ここはファイルの選択状態と表示文言の対応だけを持つ。
 */

/**
 * 一度に選べるファイルの数。 backend の上限 (読む XML・CSV の数。 ZIP の中のファイルも 1 件ずつ数える) とは単位が違い、
 * 画面は選んだファイル (ZIP は 1 つ) を数えるので、 ZIP の中が多いと確認で backend が上限超えにする。
 */
export const MAX_IMPORT_FILES = 20

/** 選んだファイル 1 つ。 選んだ時にすぐ受け取り URL を取って PUT し、 取込 token を持つ。 */
export type SelectedImportFile =
  | { id: number; name: string; status: 'uploading' }
  | { id: number; name: string; status: 'uploaded'; token: string }
  | { id: number; name: string; status: 'failed'; tooLarge: boolean }

/** 確認に進めるか: 1 つ以上あり、 全部アップロード済み (アップロード中・失敗は進めない)。 */
export function canConfirmFiles(files: SelectedImportFile[]): boolean {
  return files.length > 0 && files.every((f) => f.status === 'uploaded')
}

/** アップロード済みのファイルの token (選んだ順)。 */
export function uploadedTokens(files: SelectedImportFile[]): string[] {
  return files.flatMap((f) => (f.status === 'uploaded' ? [f.token] : []))
}

/**
 * 誤りの出たファイルを外した残り。 誤りのファイル名は、 選んだファイルそのもの (XML・CSV) なら
 * その名前、 ZIP の中のファイルなら中の名前で返るので、 名前が一致するものだけを外す。
 */
export function withoutFilesNamed(
  files: SelectedImportFile[],
  names: ReadonlySet<string>,
): SelectedImportFile[] {
  return files.filter((f) => !names.has(f.name))
}

/**
 * ファイルの誤りの理由 → 文言の key。 誤りの理由の enum は他社と自社の取込で共有なので、 文言の
 * 置き場 (`prefix`: 他社の取込の文言か自社の取込の文言か) だけが違う。
 */
export function issueReasonKeys(
  prefix: string,
): Record<SplitSupplyPartnerNoticeImportIssueReason, string | undefined> {
  const key = (name: string): string => `${prefix}.errors.reason.${name}`
  return {
    [SplitSupplyPartnerNoticeImportIssueReason.UNSPECIFIED]: undefined,
    [SplitSupplyPartnerNoticeImportIssueReason.UNSUPPORTED_FORMAT]:
      key('unsupportedFormat'),
    [SplitSupplyPartnerNoticeImportIssueReason.NESTED_ZIP]: key('nestedZip'),
    [SplitSupplyPartnerNoticeImportIssueReason.BROKEN_FILE]: key('brokenFile'),
    [SplitSupplyPartnerNoticeImportIssueReason.UNSUPPORTED_ENCODING]: key(
      'unsupportedEncoding',
    ),
    [SplitSupplyPartnerNoticeImportIssueReason.UNSUPPORTED_MESSAGE]:
      key('unsupportedMessage'),
    [SplitSupplyPartnerNoticeImportIssueReason.SECTION_MISMATCH]:
      key('sectionMismatch'),
    [SplitSupplyPartnerNoticeImportIssueReason.UNKNOWN_DESTINATION]:
      key('unknownDestination'),
    [SplitSupplyPartnerNoticeImportIssueReason.INVALID_DATE]:
      key('invalidDate'),
    [SplitSupplyPartnerNoticeImportIssueReason.INVALID_SLOT]:
      key('invalidSlot'),
    [SplitSupplyPartnerNoticeImportIssueReason.VALUE_INVALID]:
      key('valueInvalid'),
    [SplitSupplyPartnerNoticeImportIssueReason.VALUE_OUT_OF_RANGE]:
      key('valueOutOfRange'),
    [SplitSupplyPartnerNoticeImportIssueReason.SLOT_COUNT]: key('slotCount'),
    [SplitSupplyPartnerNoticeImportIssueReason.DUPLICATE_SLOT]:
      key('duplicateSlot'),
    [SplitSupplyPartnerNoticeImportIssueReason.DUPLICATE_POINT]:
      key('duplicatePoint'),
    [SplitSupplyPartnerNoticeImportIssueReason.DATE_OUT_OF_PERIOD]:
      key('dateOutOfPeriod'),
    [SplitSupplyPartnerNoticeImportIssueReason.HEADER_INVALID]:
      key('headerInvalid'),
    [SplitSupplyPartnerNoticeImportIssueReason.MIXED_KINDS]: key('mixedKinds'),
    [SplitSupplyPartnerNoticeImportIssueReason.TOO_MANY_POINTS]:
      key('tooManyPoints'),
    [SplitSupplyPartnerNoticeImportIssueReason.DATE_CLOSED]: key('dateClosed'),
  }
}
